import { useMutation } from '@apollo/client/react';
import { useNavigate } from 'react-router';
import type { GraphQLFormattedError } from 'graphql';
import { useTranslation } from 'react-i18next';
import { CombinedGraphQLErrors } from '@apollo/client/errors';

import { ChangePasswordDocument } from 'graphql/types';
import { ChangePasswordForm } from 'user/components/forms/ChangePasswordForm';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { getErrorMessage } from 'common/utils/getErrorMessage';
import { getUrl } from 'route/utils/getUrl';

interface IChangePasswordErrors {
    invalidError?: GraphQLFormattedError;
    otherErrors: GraphQLFormattedError[];
}

const parseError = (errors: readonly GraphQLFormattedError[]) =>
    // Split the invalidError and the rest so we can show the invalid error as a
    // form field error.
    errors.reduce(
        (acc: IChangePasswordErrors, e: GraphQLFormattedError) => {
            if (e?.extensions?.code === 'PasswordInvalid') {
                return { ...acc, invalidError: e };
            }

            return {
                ...acc,
                otherErrors: [...acc.otherErrors, e],
            };
        },
        {
            invalidError: undefined,
            otherErrors: [],
        }
    ) as IChangePasswordErrors;

interface IProps {
    onSuccess?(): void;
}

export const ChangePassword = ({ onSuccess }: IProps) => {
    const { t: translate } = useTranslation();
    const navigate = useNavigate();
    const [displaySnackbar] = useSnackbar();

    const [changePassword] = useMutation(ChangePasswordDocument);

    return (
        <ChangePasswordForm
            onSubmit={async (value, helpers) => {
                const { password } = value;

                try {
                    await changePassword({
                        variables: { password },
                    });
                } catch (error) {
                    if (!CombinedGraphQLErrors.is(error)) throw error;

                    const { invalidError, otherErrors } = parseError(
                        error.errors
                    );

                    // Set field error when an "PasswordInvalid" is returned
                    if (invalidError) {
                        helpers.setFieldError(
                            'password',
                            JSON.parse(invalidError.message).join(', ')
                        );
                    }

                    // Show snackbar for other errors
                    otherErrors.forEach((e) => {
                        const errorMessage = getErrorMessage(e);

                        if (errorMessage) {
                            displaySnackbar(errorMessage);
                        }
                    });

                    helpers.setSubmitting(false);

                    return;
                }

                // If onSuccess is given we want to call it and stop there.
                if (onSuccess) {
                    onSuccess();

                    return;
                }

                displaySnackbar(translate('passwordChanged'), {
                    variant: 'success',
                });

                // Redirect to Home when password has been changed
                navigate(getUrl('HOME'));
            }}
        />
    );
};
